import { useQueryClient } from '@tanstack/react-query';
import { camelCase } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { filter, map, tap } from 'rxjs';

import { QUERY_KEY_CONST } from '@/constant';
import { STATUS_COLOR_MAP } from '@/constant/taskConstant';
import {
  useRequestTaskPreAnnotationMutation,
  useTenantUsageQuery,
} from '@/hook';
import {
  AiAnno,
  Info,
  VButton,
  VFlex,
  VStack,
  VStatusBadge,
  type VStatusBadgeProps,
  VTooltip,
  cn,
  formatDate,
  useSubscriptions,
} from '@/package';
import { useWebSocket } from '@/provider';
import type { Modeling, Operation, Task } from '@/type';
import { mapTaskStateToWorkProgress } from '@/util';

import { PreAnnotationModal } from '../PreAnnotation';

type PreAnnotationOperationProps = {
  task: Task;
};

const PreAnnotationOperation = ({ task }: PreAnnotationOperationProps) => {
  const { t } = useTranslation();
  const [currentCount, setCurrentCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const { message$ } = useWebSocket();
  const [totalPreAnnotationNum, setTotalPreAnnotationNum] = useState(0);
  const { mutateAsync: runPreAnnotation } =
    useRequestTaskPreAnnotationMutation();
  const { data: tenantUsage } = useTenantUsageQuery();
  const queryClient = useQueryClient();
  const [preAnnotationModalVisible, setPreAnnotationModalVisible] =
    useState(false);

  const taskState = mapTaskStateToWorkProgress(task);
  const operation = task?.operation?.find(
    op => op.operationType === 'AIAnnotation',
  );
  const state = (() => {
    const opsState = operation?.operationState;

    if (opsState === 'Running') return 'In progress';
    if (opsState === 'Completed') return 'Completed';
    if (opsState === 'Failed') return 'Failed';
    if (opsState === 'Canceled') return 'Not started';

    return 'Not started';
  })();

  const disabled = (() => {
    const isSomethingRunning = task?.operation?.some(
      op => op.operationState === 'Running',
    );
    return (
      isSomethingRunning || state === 'In progress' || taskState === 'Completed'
    );
  })();

  const onClick = async () => {
    setPreAnnotationModalVisible(true);
  };

  const onConfirm = (modelingID: Modeling['id'], useSession: boolean) => {
    return runPreAnnotation({ taskID: task.id, modelingID, useSession })
      .then(res => {
        setTotalPreAnnotationNum(res.data.resultData);
        queryClient.invalidateQueries({
          queryKey: QUERY_KEY_CONST.TASK_KEYS.getDetail(),
        });
      })
      .finally(() => {
        setPreAnnotationModalVisible(false);
      });
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    totalCount && setTotalPreAnnotationNum(Number(totalCount));
  }, [totalCount]);

  useSubscriptions(() => {
    if (!message$) return [];

    return [
      message$
        .pipe(
          filter(message => !!message),
          filter(message => message.eventType === 'MiriFrame'),
          map(
            message =>
              message.payload as {
                currentCount: number;
                frames: { [key: number]: string };
                projectName: string;
                reportMsg: string;
                taskID: string;
                taskName: string;
                time: string;
                totalCount: number;
              },
          ),
          filter(payload => Number(payload.taskID) === task.id),
          tap(payload => {
            setCurrentCount(payload.currentCount);
            setTotalCount(payload.totalCount);
          }),
        )
        .subscribe(),
    ];
  }, [message$, task]);

  return (
    <>
      <VStack
        className="flex-1 rounded-md border border-gray-300 px-4 py-2"
        gap={0.5}
      >
        <VFlex justify="between" items="center">
          <VFlex gap={0.25} className="items-center">
            <AiAnno className="h-5 w-5 text-primary-500" />
            <div className="text-body-lg font-semibold text-gray-600">
              AI-annotation
            </div>
          </VFlex>

          {state && (
            <VFlex items="center" gap={0.25}>
              <PreAnnotationOperationInfoTooltip operation={operation} />
              <VStatusBadge
                size="sm"
                variant={
                  camelCase(state.toLowerCase()) as VStatusBadgeProps['variant']
                }
              >
                {state}
              </VStatusBadge>
            </VFlex>
          )}
        </VFlex>

        <VFlex
          justify="between"
          items="center"
          gap={0.5}
          className="h-[38px] pl-6"
        >
          {state !== 'In progress' && (
            <>
              <VFlex className="flex-1" gap={0.25} items="center">
                <VStack className="flex-1 gap-[2px]">
                  <div className="text-body-sm text-gray-500">
                    Used/Available
                  </div>
                  <VFlex gap={0.25}>
                    <span className="text-body-md font-semibold text-gray-600">
                      {/* {task.info?.totalAiAnnotationCount ?? 0} */}
                      {(tenantUsage?.usage.annotationCount ?? 0) -
                        (tenantUsage?.usage.reward?.aiAnnotationCount ?? 0)}
                    </span>
                    <span className="text-body-md text-gray-600">
                      {/* / {task.info?.limitAiAnnotationCount ?? 0} */}/{' '}
                      {tenantUsage?.usageLimit.annotationUsageLimit ?? 0}
                    </span>
                  </VFlex>
                </VStack>

                <VStack className="flex-1 gap-[2px]">
                  <div className="text-body-sm text-gray-500">
                    Used in this task
                  </div>
                  <VFlex gap={0.25}>
                    <span className="text-body-md font-semibold text-gray-600">
                      {task.info?.aiAnnotationCount ?? 0}
                    </span>
                  </VFlex>
                </VStack>
              </VFlex>
              <VButton
                disabled={disabled}
                outlined
                size="md"
                variant="secondary"
                onClick={onClick}
              >
                <AiAnno className="h-4 w-4" />
                <div>{t('task.aiAnnotationRunButtonText.ciliotomy')}</div>
              </VButton>
            </>
          )}
          {state === 'In progress' && (
            <>
              <div
                style={{ color: STATUS_COLOR_MAP[state] }}
                className="text-body-xl font-semibold"
              >
                {t('development.processing.hebetate')}
                {currentCount && totalPreAnnotationNum
                  ? ` ${currentCount} of ${totalPreAnnotationNum}`
                  : ''}
                ...
              </div>
              {/* <Warning className="h-4 w-4" /> */}
            </>
          )}
        </VFlex>
      </VStack>
      <PreAnnotationModal
        task={task}
        enabledTrackSession
        visible={preAnnotationModalVisible}
        setVisible={setPreAnnotationModalVisible}
        onConfirm={onConfirm}
      />
    </>
  );
};

const PreAnnotationOperationInfoTooltip = ({
  operation,
}: {
  operation?: Operation;
}) => {
  const content = (() => {
    if (operation?.operationState === 'Completed') {
      return `Last completed: ${formatDate(operation.updatedAt)}`;
    }
    if (operation?.operationState === 'Failed') {
      return `Failed due to ${operation.message}
      (Last failure: ${formatDate(operation.updatedAt)})`;
    }
  })();

  if (!['Completed', 'Failed'].includes(operation?.operationState ?? ''))
    return <></>;

  return (
    <VTooltip content={content} placement="bottom">
      <div className="size-fit rounded-full">
        <Info
          className={cn('size-4', {
            'text-info-500': operation?.operationState === 'Completed',
            'text-error-500': operation?.operationState === 'Failed',
          })}
        />
      </div>
    </VTooltip>
  );
};
export default PreAnnotationOperation;
